<template>
  <div class="payment-methods__buttons">
    <button
      v-if="canUseApplePay"
      class="special-button special-button--apple-pay"
      type="button"
      @click="selectMethod('ApplePay')"
    >
      Apple Pay
    </button>

    <button
      v-if="canUseGooglePay"
      class="special-button special-button--google-pay"
      type="button"
      @click="selectMethod('GooglePay')"
    >
      Google Pay
    </button>

    <div id="payment-method-gift-card" />

    <div id="payment-method-charge-room" />
  </div>
</template>

<script>
  /*
    NOTE: This integration has been a SERIOUS pain in the butt. It's purposefully
    not unit tested at this time since Shift4's docs are attrocious and the following
    code is likely to change.

    Most of the integration was implemented by pieceing together the following:
    https://s4-myportal.s3.amazonaws.com/downloads/documentation/i4go/i4go%20technical%20reference%20guide%20using%20an%20access%20token.pdf
    https://s4-myportal.s3.amazonaws.com/downloads/documentation/i4go/i4go%20technical%20reference%20guide%20using%20an%20access%20token%20and%20iframe.pdf
    https://i4m.i4go.com/js/wallets.js
    https://i4m.i4go.com/js/jquery.i4goTrueToken.js
    https://s4-myportal.s3.amazonaws.com/downloads/documentation/api/reference%20guide.pdf
  */

  import applePay from '@/services/apple-pay';
  import debounce from 'debounce';
  import errorHandlers from '@/services/error-handlers';
  import logger from '@/services/logger';
  import googlePay from '@/services/google-pay';
  import http from '@/services/http';
  import { publicIpv4 } from 'public-ip';
  import { mapGetters } from 'vuex';


  export default {
    name: 'PaymentMethodsShiftFour',

    props: {
      paymentGateway: {
        type: Object,
        required: true
      },
      total: {
        type: Number,
        required: true
      }
    },

    data() {
      return {
        accessBlock: null,
        accessBlockAmountRequested: null,
        basket: null,
        canUseApplePay: false,
        canUseGooglePay: false,
        initializationError: false,
        processingMethod: null,
        serverUrl: null,
        testEnvironmentOverride: {
          billingAddress: {
            postalCode: '65651'
          }
        },
        walletConfig: null
      };
    },

    computed: {
      ...mapGetters('locations', ['selectedLocation']),
      ...mapGetters('merchant', ['merchant']),
      ...mapGetters('orders', ['order'])
    },

    watch: {
      total: 'totalPriceChangeHandler'
    },

    created() {
      this.createdHandler();
    },

    methods: {
      createdHandler() {
        this.initializeLibrary();
      },

      async initializeLibrary() {
        try {
          await this.$nextTick();

          await this.fetchAccessBlock();
          await this.fetchWalletInfo();

          this.detectApplePay();
          this.detectGooglePay();
        }

        catch (error) {
          errorHandlers.silent(error);
          this.initializationError = true;
        }

        finally {
          this.$emit('set-loading-status', false);
        }
      },

      async fetchAccessBlock() {
        try {
          this.accessBlockAmountRequested = this.total;

          const body = {
            data: {
              i4go_basket: {
                OrderDetails: {
                  Amount: Math.round(this.total * 100),
                  OrderNumber: this.order.orderId // PROBLEM: This will cause trouble if Shift4 ever supports purchasing a gift card because we won't have an order/orderId
                }
              },
              i4go_clientIP: await publicIpv4()
            },
            storeId: this.paymentGateway.storeId
          };

          logger.info('ACCESS BLOCK REQUEST', body);

          const response = await http.post('/cardfree-api/v1/payment/tokenization-key', body);

          this.accessBlock = response.data[0].key;
          this.serverUrl = response.data[0].tokenizationUrl;

          logger.info('ACCESS BLOCK RESPONSE', response.data);
        }

        catch (error) {
          throw error;
        }
      },

      async fetchWalletInfo() {
        try {
          const response = await http.get(this.serverUrl, {
            params: {
              fuseaction: 'get.get3dsInfo',
              i4go_accessBlock: this.accessBlock
            }
          });

          logger.info('WALLET INFO', response.data);

          this.basket = response.data.basket;
          this.walletConfig = response.data.walletConfig;
        }

        catch (error) {
          throw error;
        }
      },

      async convertWalletToken(wallet) {
        try {
          const formData = new URLSearchParams();
          formData.append('fuseaction', 'api.jsonPostCardEntry');
          formData.append('i4go_accessBlock', this.accessBlock);

          logger.info('CONVERT', wallet);

          if (this.processingMethod === 'apple-pay') {
            formData.append('i4go_applePayToken', JSON.stringify(wallet));
          }

          else if (this.processingMethod === 'google-pay') {
            formData.append('i4go_googlePayToken', JSON.stringify(wallet));
          }

          const response = await http.post(`${this.serverUrl}?fuseaction=api.jsonPostCardEntry`, formData, {
            'Content-Type': 'application/x-www-form-urlencoded'
          });

          const extendedCardData = response.data.i4go_extendedcarddata;
          const token = response.data.i4go_uniqueid;

          if (!token) {
            throw new Error('Failed to receive payment token.');
          }

          logger.info('CONVERT SUCCESS', response.data);

          return {
            extendedCardData,
            token
          };
        }

        catch (error) {
          throw error;
        }
      },

      detectApplePay() {
        if (this.paymentGateway.methods.applePay) {
          const isApplePaySupported = applePay.isSupported();
          this.canUseApplePay = isApplePaySupported;
          if (isApplePaySupported) this.$emit('set-enabled-payment-method', 'apple-pay');
        }
      },

      async detectGooglePay() {
        if (this.paymentGateway.methods.googlePay) {
          const request = this.returnGooglePayRequest();
          const isGooglePaySupported = await googlePay.isSupported({
            environment: this.walletConfig.googlePay.environment,
            paymentGateway: this.paymentGateway,
            request: request.isSupported
          });
          this.canUseGooglePay = isGooglePaySupported;
          if (isGooglePaySupported) this.$emit('set-enabled-payment-method', 'google-pay');
        }
      },

      refreshAccessBlock: debounce(async function refreshAccessBlock() {
        try {
          this.$emit('set-loading-status', true);

          await this.fetchAccessBlock();
          await this.fetchWalletInfo();
        }

        catch (error) {
          errorHandlers.silent(error);
        }

        finally {
          this.$emit('set-loading-status', false);
        }
      }, 500),

      returnApplePayRequest() {
        const request = {
          countryCode: this.walletConfig.countryCode,
          currencyCode: this.walletConfig.currencyCode,
          merchantCapabilities: ['supports3DS'],
          requiredBillingContactFields: ['postalAddress', 'name'],
          requiredShippingContactFields: ['email', 'phone'],
          supportedNetworks: this.walletConfig.applePay.supportedNetworks,
          total: {
            amount: this.total.toString(),
            label: this.merchant.name,
            type: 'final'
          }
        };

        logger.info('APPLE PAY CONFIG', request);

        return request;
      },

      returnGooglePayRequest() {
        const baseRequest = {
          apiVersion: 2,
          apiVersionMinor: 0
        };

        const cardPaymentMethod = {
          parameters: {
            allowedAuthMethods: this.walletConfig.googlePay.allowedAuthMethods,
            allowedCardNetworks: this.walletConfig.googlePay.allowedCardNetworks,
            billingAddressParameters: {
              format: 'FULL',
              phoneNumberRequired: true
            },
            billingAddressRequired: true
          },
          tokenizationSpecification: {
            parameters: {
              gateway: this.walletConfig.googlePay.gateway,
              gatewayMerchantId: this.walletConfig.merchantID.toString()
            },
            type: 'PAYMENT_GATEWAY'
          },
          type: 'CARD'
        };

        const isSupported = {
          ...baseRequest,
          allowedPaymentMethods: [cardPaymentMethod]
        };

        const payment = {
          ...baseRequest,
          allowedPaymentMethods: [cardPaymentMethod],
          emailRequired: true,
          merchantInfo: {
            authJwt: this.walletConfig.googlePay.authJwt,
            merchantId: this.walletConfig.googlePay.merchantId,
            merchantName: this.walletConfig.merchantName,
            merchantOrigin: this.walletConfig.googlePay.merchantDomain
          },
          transactionInfo: {
            countryCode: this.walletConfig.countryCode,
            currencyCode: this.walletConfig.currencyCode,
            totalPrice: this.total.toString(),
            totalPriceLabel: 'Total',
            totalPriceStatus: 'FINAL'
          }
        };

        logger.info('GOOGLE PAY CONFIG', {
          isSupported,
          payment
        });

        return {
          isSupported,
          payment
        };
      },

      selectMethod(method) {
        this.$emit('before-submit', {
          method,
          onValidated: this.submitMethod
        });
      },

      submitMethod(method) {
        if (method === 'ApplePay') {
          this.submitApplePay();
        }

        else if (method === 'GooglePay') {
          this.submitGooglePay();
        }
      },

      async submitApplePay() {
        try {
          const event = await applePay.requestPayment({
            merchantValidator: this.validateApplePayMerchant,
            paymentGateway: this.paymentGateway,
            request: this.returnApplePayRequest(),
            total: this.total,
            version: 3
          });

          this.processingMethod = 'apple-pay';

          const { extendedCardData, token } = await this.convertWalletToken(event.payment);

          this.$emit('submitted', {
            contactInfo: {
              email: event.payment.shippingContact.emailAddress,
              fullName: `${event.payment.billingContact.givenName} ${event.payment.billingContact.familyName}`,
              phoneNumber: event.payment.shippingContact.phoneNumber
            },
            paymentMethod: {
              event,
              extendedCardData,
              testEnvironmentOverride: this.testEnvironmentOverride,
              type: 'apple-pay',
              walletTokenOverride: token
            }
          });
        }

        catch (error) {
          this.$triggerNewRelicEvent('Shift4ApplePay', 'Error', 'Unexpected error', `Error: ${error.message}`);

          this.$emit('error', error);
        }
      },

      async submitGooglePay() {
        try {
          const request = this.returnGooglePayRequest();

          const event = await googlePay.requestPayment({
            paymentGateway: this.paymentGateway,
            request: request.payment,
            total: this.total
          });

          this.processingMethod = 'google-pay';

          const { extendedCardData, token } = await this.convertWalletToken(event);

          this.$emit('submitted', {
            contactInfo: {
              email: event.email,
              fullName: event.paymentMethodData.info.billingAddress.name,
              phoneNumber: event.paymentMethodData.info.billingAddress.phoneNumber
            },
            paymentMethod: {
              event,
              extendedCardData,
              testEnvironmentOverride: this.testEnvironmentOverride,
              type: 'google-pay',
              walletTokenOverride: token
            }
          });
        }

        catch (error) {
          this.$triggerNewRelicEvent('Shift4GooglePay', 'Error', 'Unexpected error', `Error: ${error.message}`);

          this.$emit('error', error);
        }
      },

      async totalPriceChangeHandler(newValue) {
        await this.$nextTick();

        if (newValue === this.accessBlockAmountRequested) {
          return;
        }

        this.refreshAccessBlock();
      },

      async validateApplePayMerchant(options) {
        try {
          const formData = new URLSearchParams();
          formData.append('validationURL', options.event.validationURL);
          formData.append('i4go_accessBlock', this.accessBlock);

          const response = await http.post(`https://${this.walletConfig.providerDomain}/index.cfm?fuseaction=ws.applePaySession`, formData, {
            'Content-Type': 'application/x-www-form-urlencoded'
          });

          logger.info('VALIDATED AP MERCHANT', response.data);

          return response;
        }

        catch (error) {
          throw error;
        }
      }
    }
  };
</script>
